import { h, Component, ComponentChildren } from "preact";

import { FLAG_USE_CONSOLE_LOGGER } from "../../foundation/constants";
import CSSProperties from "../../foundation/model/CSSProperties";
import { deepDiffer, shallowDiffer } from "../../foundation/utils/diff";
import { areRectsIntersecting } from "../../foundation/utils/legacy";
import noderef from "../../foundation/utils/noderef";
import NodeRefs from "../../foundation/utils/NodeRefs";
import throttle from "../../foundation/utils/throttle";

type observerCbExposed<T> = (entry: IntersectionObserverEntry, observedItem: T) => void;

interface Callbacks<T> {
    onEnter: observerCbExposed<T>;
    onLeave?: observerCbExposed<T>;
    onAboutToEnter?: observerCbExposed<T>;
}
interface Props<T> extends Callbacks<T> {
    targetClass?: string;
    dataClientRect?: string; // This is a test only needed custom attribute. (Has no effect for real use cases)
    targetStyle?: CSSProperties;
    children?: ComponentChildren;
    observedItem?: T;
    options?: IntersectionObserverInit;
    offScreenMargin?: number;
}
interface State {
    observedEntry: IntersectionObserverEntry | null;
}

type Options = IntersectionObserverInit | undefined;
type Rect = Omit<DOMRectReadOnly, "toJSON" | "x" | "y">;

function expandRectByMargin(rect: DOMRectReadOnly, expandMargin = 0): Rect {
    const newRect = {
        top: rect.top - expandMargin,
        right: rect.right + expandMargin,
        bottom: rect.bottom + expandMargin,
        left: rect.left - expandMargin
    };

    return {
        ...newRect,
        width: newRect.right - newRect.left,
        height: newRect.bottom - newRect.top
    };
}

/*
Value of isIntersecting is depended on threshold value in Chrome.
However, in Safari, Firefox and Polyfill we just need to check also intersectionRatio.
Realized different behavior: https://github.com/w3c/IntersectionObserver/issues/345
inView variable is for fixing the behaviour between browsers.
*/
function isInView(isIntersecting: boolean, intersectionRatio: number, options: Options): boolean {
    const threshold = options?.threshold ?? 0;
    const minThreshold = Array.isArray(threshold) ? Math.min(...threshold) : threshold;

    return isIntersecting && intersectionRatio >= minThreshold;
}

/**
 IntersectionObserverTarget Component is to observe changes in the intersection of a target element or within the document's viewport.
 Basically exposes 3 callbacks for 3 different states of target element observed:
 1) onEnter -> Called when target element is in the viewport or intersecting with the root.
 2) onLeave -> Called when target element is offscreen or not intersecting with the root.
 3) onAboutToEnter -> Called when target element is offscreen or not intersecting with the root but it's inside offScreenMargin area.
 */

class IntersectionObserverTarget<T> extends Component<Props<T>, State> {
    nodeRefs: NodeRefs | undefined;
    observer: null | IntersectionObserver = null;
    override state = { observedEntry: null };

    observeIntersection = () => {
        const targetElem = this.nodeRefs?.["intersectionTarget"];

        if (!targetElem) {
            if (FLAG_USE_CONSOLE_LOGGER) {
                throw new Error("Target element is missing to be observed. Be sure, you did not mutate it.");
            } else {
                return;
            }
        }

        const { options } = this.props;
        const intersectionHandler = this.handleIntersect(options);

        this.observer = new IntersectionObserver(intersectionHandler, options);
        this.observer.observe(targetElem);
    };

    unObserveIntersection = () => {
        const targetElem = this.nodeRefs?.["intersectionTarget"];

        if (!targetElem || !this.observer || !this.observer.unobserve) {
            return;
        }

        this.observer.unobserve(targetElem);
    };

    // If we need to track offScreen items that are inside offScreenMargin area with onAboutToEnter callback.
    listenScrollIfNecessary = () => {
        const { onAboutToEnter, options: { root } = {} } = this.props;

        if (onAboutToEnter && root) {
            root.addEventListener("scroll", this.handleScrollPositionChange);
        }
    };

    removeListeningScrollIfNecessary = () => {
        const { onAboutToEnter, options: { root } = {} } = this.props;

        if (onAboutToEnter && root) {
            root.removeEventListener("scroll", this.handleScrollPositionChange);
        }
    };

    handleAboutToEnter = (entry: IntersectionObserverEntry, currentClientRect?: DOMRectReadOnly | null) => {
        const { options } = this.props;
        const { boundingClientRect, rootBounds: rootRect, isIntersecting, intersectionRatio } = entry;
        const inView = isInView(isIntersecting, intersectionRatio, options);

        // check only for offscreen items
        if (inView) {
            return;
        }

        const targetRect = currentClientRect ?? boundingClientRect;
        const { offScreenMargin, onAboutToEnter, observedItem } = this.props;
        const expandedRootRect = expandRectByMargin(rootRect!, offScreenMargin);
        const isAboutToEnter = areRectsIntersecting(targetRect, expandedRootRect as DOMRectReadOnly);

        if (isAboutToEnter && onAboutToEnter) {
            onAboutToEnter(entry, observedItem!);
        }
    };

    handleOffscreen = (entry: IntersectionObserverEntry, observedItem?: T) => {
        const { onLeave, onAboutToEnter } = this.props;

        if (onLeave) {
            onLeave(entry, observedItem!);
        }

        if (onAboutToEnter) {
            this.handleAboutToEnter(entry);
        }
    };

    handleVisible = (entry: IntersectionObserverEntry, observedItem?: T) => {
        const { onEnter } = this.props;

        if (onEnter) {
            onEnter(entry, observedItem!);
        }
    };

    handleScrollPositionChange = throttle(() => {
        const { observedEntry } = this.state;

        if (!observedEntry) {
            return;
        }

        const currentClientRect = (observedEntry as unknown as IntersectionObserverEntry)
            ?.target
            ?.getBoundingClientRect();

        this.handleAboutToEnter(
            (observedEntry as unknown as IntersectionObserverEntry),
            currentClientRect
        );
    }, 500);

    handleIntersect(options: Options) {
        return (entries: IntersectionObserverEntry[]) => {
            const { observedItem } = this.props;
            entries.forEach((entry: IntersectionObserverEntry) => {
                const { isIntersecting, intersectionRatio } = entry;
                const inView = isInView(isIntersecting, intersectionRatio, options);

                this.setState({ observedEntry: entry });

                // exposing onEnter or onLeave and onAboutToEnter callbacks
                inView
                    ? this.handleVisible(entry, observedItem)
                    : this.handleOffscreen(entry, observedItem);
            });
        };
    }

    override componentDidMount() {
        this.observeIntersection();
        this.listenScrollIfNecessary();
    }

    override componentDidUpdate(prevProps: Props<T>) {
        const { options } = this.props;

        const { threshold: prevThreshold, ...prevOptions } = prevProps.options ?? {};
        const { threshold: currentThreshold, ...currentOptions } = options ?? {};

        // `threshold` can be an array, so we need to check it deeply.
        const optionsChanged = shallowDiffer(prevOptions, currentOptions) ||
            deepDiffer(prevThreshold, currentThreshold);

        // when options are changed we need to un-observe then re-observe intersections with new options provided.
        if (optionsChanged) {
            this.unObserveIntersection();
            setTimeout(this.observeIntersection);
        }
    }

    override componentWillUnmount() {
        this.unObserveIntersection();
        this.removeListeningScrollIfNecessary();
    }

    render() {
        const {
            dataClientRect,
            targetClass,
            targetStyle,
            children
        } = this.props;

        return (
            <div
                data-clientRect={dataClientRect}
                ref={noderef(this, "intersectionTarget")}
                style={targetStyle}
                className={targetClass}>
                {children}
            </div>
        );
    }
}

export default IntersectionObserverTarget;
